import { useEffect, useState } from "react";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import { UserRole } from "../../../app/userSlice";
import {
	ReportLevel,
	useGetDistrictOptionsForReportsQuery,
	useLazyGetSchoolOptionsForReportsQuery,
} from "./adminReportsApiSlice";

interface UseReportLevelOptionsArgs {
	// The ReportLevel these options should be scoped to (e.g. the max report
	// level for "Admin" tab reports, or always ReportLevel.Course for the
	// "Course" tab reports).
	level: ReportLevel;
	maxReportLevel: ReportLevel;
	// Whether this level's options/defaults are currently "active" (i.e. the
	// user is viewing the tab that these options apply to). When not active,
	// the hook still fetches options (so switching tabs is instant) but will
	// not attempt to apply defaults/URL params to the shared filter state.
	isActive: boolean;
	topLevelDistrictId?: number;
	topLevelSchoolId?: number;
	districtFilter: number;
	schoolFilter: number;
	districtIdParam: number | null;
	schoolIdParam: number | null;
	role: UserRole | null;
	userSchool: { id: number } | null;
	userDistrict: { id: number } | null;
	setDistrictFilter: (value: number) => void;
	setSchoolFilter: (value: number) => void;
}

// Fetches district + school options for a single report level (Admin or
// Course), and applies defaults/URL params to the shared district/school
// filters when this level's tab is the active one. This is parametrized so
// the near-identical Admin-level and Course-level logic doesn't need to be
// duplicated.
export function useReportLevelDistrictAndSchoolOptions({
	level,
	maxReportLevel,
	isActive,
	topLevelDistrictId,
	topLevelSchoolId,
	districtFilter,
	schoolFilter,
	districtIdParam,
	schoolIdParam,
	role,
	userSchool,
	userDistrict,
	setDistrictFilter,
	setSchoolFilter,
}: UseReportLevelOptionsArgs) {
	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);
	const [schoolOptions, setSchoolOptions] = useState<SimpleOption[]>([]);

	// Pass the SPA's current top-level district (state.user.district) as districtId so
	// the backend uses it to determine the "selected" district, instead of falling back
	// to a possibly-stale legacy MVC session value (which can be out of sync with the
	// SPA's DistrictPicker/SchoolPicker after switching districts/schools at the top level).
	const getDistrictsResult = useGetDistrictOptionsForReportsQuery({
		level,
		districtId: topLevelDistrictId,
	});
	const districts = getDistrictsResult.data;

	const [callGetSchoolsByDistrict] = useLazyGetSchoolOptionsForReportsQuery();

	// Build district options + apply default/URL-param district filter
	useEffect(() => {
		if (!districts) return;

		const options = districts.map((d) => ({ value: d.value, label: d.text }) as SimpleOption);

		if (maxReportLevel === ReportLevel.State) {
			options.unshift({ value: 0, label: "All Districts" } as SimpleOption);
		}

		setDistrictOptions(options);

		if (!isActive) return;

		// Set from URL param if present, otherwise set default if not already set
		if (districtIdParam !== null) {
			setDistrictFilter(districtIdParam);
		} else if (!districtFilter) {
			if (maxReportLevel === ReportLevel.School || maxReportLevel === ReportLevel.District) {
				const selectedDistrict = Number(
					districts.find((d) => d.selected === true)?.value ?? 0
				);
				setDistrictFilter(selectedDistrict);
			} else {
				setDistrictFilter(0);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [districts, districtIdParam, isActive]);

	// Fetch school options + apply default/URL-param school filter
	useEffect(() => {
		// skip call before ready for max level of School
		// let school admins in a school with no district pass by bypassing the
		// check for !districtFilter via checking district
		const schoolAdminWithoutDistrictContext =
			role === UserRole.SchoolAdmin && userSchool && !userDistrict;

		if (maxReportLevel === ReportLevel.School && !districtFilter) {
			if (!schoolAdminWithoutDistrictContext) return;
		}

		callGetSchoolsByDistrict(
			{
				level: maxReportLevel,
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: topLevelSchoolId,
			},
			true
		)
			.unwrap()
			.then((schools) => {
				if (!schools) return;

				const options = schools.map(
					(s) => ({ value: s.value, label: s.text }) as SimpleOption
				);

				if (maxReportLevel !== ReportLevel.School) {
					options.unshift({ value: 0, label: "All Schools" } as SimpleOption);
				}

				setSchoolOptions(options);

				if (!isActive) return;

				// Set from URL param if present, otherwise set default if not already set
				if (schoolIdParam !== null) {
					setSchoolFilter(schoolIdParam);
				} else if (!schoolFilter) {
					if (maxReportLevel === ReportLevel.School) {
						const selectedSchool = Number(
							schools.find((s) => s.selected === true)?.value ?? 0
						);
						setSchoolFilter(selectedSchool);
					} else {
						setSchoolFilter(0);
					}
				}
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [districtFilter, schoolIdParam, isActive]);

	return { districtOptions, schoolOptions };
}
